import { MouseEvent, useMemo } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { actionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { IUser } from 'src/interfaces/User';

import UserRoleChip from '../components/UserRoleChip';

interface Props {
  users: IUser[];
  canManage?: boolean;
  onCheckIn?: (id: string, e?: MouseEvent<unknown>) => void;
  onDelete: (id: string, e?: MouseEvent<unknown>) => void;
  onEdit: (id?: string, e?: MouseEvent<unknown>) => void;
}

const UsersTable = ({ users, canManage = false, onCheckIn, onDelete, onEdit }: Props) => {
  const { t } = useTranslation();

  const columns = useMemo((): GridColDef<IUser>[] => {
    const base: GridColDef<IUser>[] = [
      { field: 'name', headerName: t('form.name'), flex: 1, minWidth: 160, sortable: false },
      { field: 'email', headerName: t('form.email'), flex: 1, minWidth: 200, sortable: false },
      {
        field: 'role',
        headerName: t('users.role'),
        width: 140,
        sortable: false,
        renderCell: ({ row }) => <UserRoleChip role={row.role} />,
      },
    ];

    if (onCheckIn || canManage) {
      base.push(
        actionsColumn<IUser>(t, 'users.optionsLabel', ({ row }) => (
          <>
            {onCheckIn ? (
              <Tooltip title={t('userCheckinWizard.openFromUser')}>
                <IconButton size="small" onClick={e => onCheckIn(row.id, e)} color="primary">
                  <HowToRegIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canManage ? (
              <>
                <Tooltip title={t('users.deleteUser')}>
                  <IconButton onClick={e => onDelete(row.id, e)} color="error" size="small">
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('users.editUser')}>
                  <IconButton size="small" onClick={e => onEdit(row.id, e)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </>
        )),
      );
    }

    return base;
  }, [canManage, onCheckIn, onDelete, onEdit, t]);

  return (
    <DashboardDataGrid<IUser>
      aria-label={t('users.usersTableLabel')}
      chrome="compact"
      rows={users}
      columns={columns}
    />
  );
};

export default UsersTable;
